import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import clsx from 'clsx';

export interface AccountingTab {
  /** Absolute path this tab navigates to. */
  to: string;
  label: string;
  /**
   * Also treat these path prefixes as this tab — the reconciliation
   * workspace is still the Reconciliations tab, and losing the
   * highlight when you open one makes the strip look broken.
   */
  matches?: string[];
}

interface Props {
  tabs: AccountingTab[];
}

/**
 * The tab strip for the accounting area.
 *
 * Deliberately a second component rather than a reuse of `BillingTabs`.
 * They are identical today, and the first time one of them needs a
 * badge — an unreconciled count on Reconciliations is the obvious
 * candidate — a shared component becomes a prop nobody in billing
 * wants. The duplication is eleven lines of JSX; the coupling would be
 * permanent.
 */
const AccountingTabs: React.FC<Props> = ({ tabs }) => {
  const { pathname } = useLocation();

  const isCurrent = (tab: AccountingTab): boolean =>
    pathname === tab.to ||
    (tab.matches ?? []).some((prefix) => pathname.startsWith(prefix));

  return (
    <div className="flex flex-wrap gap-2 border-b border-zinc-200 dark:border-zinc-700 mb-6">
      {tabs.map((tab) => (
        <Link
          key={tab.to}
          to={tab.to}
          className={clsx(
            'px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
            isCurrent(tab)
              ? 'border-sky-500 text-sky-600 dark:text-sky-400'
              : 'border-transparent text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200'
          )}
        >
          {tab.label}
        </Link>
      ))}
    </div>
  );
};

export default AccountingTabs;
